import { useEffect, useState, useCallback, useMemo } from 'react';
import { Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { getGenerationPoints } from '../services/api';

// Alt-energy generation-site overlay (#997).
//
// Renders built wind / solar / geothermal points from the
// /generation/points GeoJSON endpoint. One marker style per tech
// (wind = teal, solar = amber, geothermal = red-orange — matched to the
// API's `properties.color` and the #997 legend spec), sized by
// `capacity_mw`. Clustered at low zoom via react-leaflet-cluster, the
// same clustering the parcel markers use. A per-tech on/off toggle lets
// the user isolate a single technology without leaving the overlay.
//
// Visibility: self-managed via react-leaflet `overlayadd` /
// `overlayremove` events keyed on OVERLAY_NAME, mirroring InsarLayer.
// The component mounts inside a `<LayersControl.Overlay
// name={OVERLAY_NAME}>` block in MapView; toggling the standard
// map-overlay checkbox flips `enabled` here with no external state.

const OVERLAY_NAME = 'Alt-energy generation sites';

// One color per tech. Kept in sync with the backend TECH_COLORS palette
// (generation_sites.py) so the API-supplied `properties.color` and this
// legend agree by default. The API ships a `color` per feature, but we
// key off `tech` locally so a legacy/unknown tech still resolves to the
// shared palette rather than the API's gray fallback.
export const TECH_META = {
  wind:       { color: '#1ABC9C', label: 'Wind' },
  solar:      { color: '#F39C12', label: 'Solar' },
  geothermal: { color: '#E67E22', label: 'Geothermal' },
};

const ALL_TECHS = ['wind', 'solar', 'geothermal'];

function techColor(tech) {
  return (TECH_META[tech] && TECH_META[tech].color) || '#7F8C8D';
}

// Marker radius scales with plant capacity (MW). Utility-scale plants
// span roughly 1 MW (small solar) to 1000+ MW (large wind/geothermal),
// so a log scale keeps the smallest sites visible without letting the
// biggest dominate the viewport. Clamped to [6, 18] px.
export function radiusForCapacity(capacityMw) {
  if (capacityMw == null || !(capacityMw > 0)) return 6;
  const r = 6 + Math.log10(capacityMw + 1) * 4;
  return Math.max(6, Math.min(18, Math.round(r)));
}

// Per-tech divIcon factory: a filled circle whose diameter tracks
// capacity. Cached by (tech, radius) so panning/zooming doesn't churn
// icon instances (react-leaflet re-uses the reference).
const _iconCache = {};
export function getGenerationIcon(tech, radius) {
  const key = `${tech}-${radius}`;
  if (_iconCache[key]) return _iconCache[key];
  const color = techColor(tech);
  const d = radius * 2;
  const svg = `<svg width="${d}" height="${d}" viewBox="0 0 ${d} ${d}" xmlns="http://www.w3.org/2000/svg">
    <circle cx="${radius}" cy="${radius}" r="${radius - 1}" fill="${color}" fill-opacity="0.85" stroke="#333" stroke-width="1"/>
  </svg>`;
  const icon = L.divIcon({
    html: svg,
    className: '',
    iconSize: [d, d],
    iconAnchor: [radius, radius],
    popupAnchor: [0, -radius],
  });
  // Tag the icon with its tech/radius. Harmless on the real Leaflet icon
  // (extra own-props are ignored) and lets the component-level tests read
  // back the encoding without parsing the SVG string.
  if (icon && typeof icon === 'object') {
    icon.__tech = tech;
    icon.__radius = radius;
  }
  _iconCache[key] = icon;
  return icon;
}

function fmtCapacity(mw) {
  if (mw == null) return '—';
  return `${Number(mw).toLocaleString(undefined, { maximumFractionDigits: 1 })} MW`;
}

// Stable cluster icon factory (mirrors MapView.createClusterIcon) so the
// generation markers cluster with the same visual language as parcels.
const createGenClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let diameter = 36;
  if (count >= 100) { size = 'large'; diameter = 50; }
  else if (count >= 10) { size = 'medium'; diameter = 42; }
  return L.divIcon({
    html: `<div class="cluster-icon cluster-${size}"><span>${count >= 1000 ? Math.round(count / 1000) + 'k' : count}</span></div>`,
    className: 'custom-cluster',
    iconSize: L.point(diameter, diameter),
  });
};

function GenerationLayer() {
  const map = useMap();
  const [enabled, setEnabled] = useState(false);
  // Per-tech visibility. All techs on by default; the in-overlay toggle
  // flips individual entries so a user can isolate e.g. wind only.
  const [activeTechs, setActiveTechs] = useState(() => new Set(ALL_TECHS));
  const [features, setFeatures] = useState([]);

  const load = useCallback(async () => {
    if (!enabled) {
      setFeatures([]);
      return;
    }
    const b = map.getBounds();
    const bbox = `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`;
    try {
      // Fetch ALL techs (no server-side `tech` filter) and gate
      // per-tech client-side, so toggling a tech is instant and doesn't
      // re-hit the API. The response is capped at `limit` server-side.
      const data = await getGenerationPoints(bbox, null, 5000);
      setFeatures(data?.features || []);
    } catch (err) {
      // Silent failure: an empty overlay is a valid "no data" signal and
      // a transient API blip shouldn't crash the map.
      // eslint-disable-next-line no-console
      console.warn('Generation-sites overlay load failed:', err);
      setFeatures([]);
    }
  }, [enabled, map]);

  useMapEvents({
    moveend: load,
    zoomend: load,
    overlayadd: (e) => { if (e.name === OVERLAY_NAME) setEnabled(true); },
    overlayremove: (e) => { if (e.name === OVERLAY_NAME) setEnabled(false); },
  });

  useEffect(() => { load(); }, [load]);

  const toggleTech = useCallback((tech) => {
    setActiveTechs((prev) => {
      const next = new Set(prev);
      if (next.has(tech)) next.delete(tech); else next.add(tech);
      return next;
    });
  }, []);

  const visibleFeatures = useMemo(
    () => features.filter((f) => activeTechs.has(f?.properties?.tech)),
    [features, activeTechs],
  );

  if (!enabled) return null;

  return (
    <>
      {/* Per-tech on/off toggle, rendered as a compact control anchored
          near the layer control. Only shown while the overlay is on. */}
      <GenerationTechToggle activeTechs={activeTechs} onToggle={toggleTech} />

      {visibleFeatures.length > 0 && (
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          spiderfyOnMaxZoom
          showCoverageOnHover={false}
          iconCreateFunction={createGenClusterIcon}
          disableClusteringAtZoom={13}
        >
          {visibleFeatures.map((f) => {
            const p = f.properties || {};
            const coords = f.geometry?.coordinates || [];
            const lon = coords[0];
            const lat = coords[1];
            if (lat == null || lon == null) return null;
            const radius = radiusForCapacity(p.capacity_mw);
            return (
              <Marker
                key={`gen-${p.id}`}
                position={[lat, lon]}
                icon={getGenerationIcon(p.tech, radius)}
              >
                <Popup maxWidth={280} minWidth={200}>
                  <div style={{ fontSize: '0.82rem', lineHeight: 1.45 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span
                        style={{
                          display: 'inline-block', width: 10, height: 10,
                          borderRadius: '50%', background: techColor(p.tech),
                          border: '1px solid #333', flexShrink: 0,
                        }}
                      />
                      <span style={{ fontWeight: 700, color: '#1a1a2e' }}>
                        {p.name || 'Unnamed site'}
                      </span>
                    </div>
                    <div style={{ color: '#666', marginBottom: 6, textTransform: 'capitalize' }}>
                      {(TECH_META[p.tech] && TECH_META[p.tech].label) || p.tech || '—'}
                    </div>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                      <tbody>
                        <tr>
                          <td style={{ paddingRight: 8, color: '#666' }}>Operator</td>
                          <td style={{ textAlign: 'right' }}>{p.operator || '—'}</td>
                        </tr>
                        <tr>
                          <td style={{ paddingRight: 8, color: '#666' }}>Capacity</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtCapacity(p.capacity_mw)}</td>
                        </tr>
                        <tr>
                          <td style={{ paddingRight: 8, color: '#666' }}>Year online</td>
                          <td style={{ textAlign: 'right' }}>{p.year_online || '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                    {p.source && (
                      <div style={{ marginTop: 6, fontSize: '0.68rem', color: '#888' }}>
                        Source: {p.source}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MarkerClusterGroup>
      )}
    </>
  );
}

// Per-tech on/off toggle. Rendered inside the map (absolute-positioned)
// only while the overlay is enabled. Mirrors the compact overlay-control
// styling used elsewhere in MapView.
// `top: 64, left: 62` tucks it just below the view-mode toggle and to the
// right of Leaflet's native zoom control (top-left corner) so it no longer
// covers the +/- zoom buttons (#1037).
export function GenerationTechToggle({ activeTechs, onToggle }) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 64,
        left: 62,
        zIndex: 1000,
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid #ccc',
        borderRadius: 4,
        padding: '6px 8px',
        fontSize: '0.72rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>Generation tech</div>
      {ALL_TECHS.map((tech) => {
        const on = activeTechs.has(tech);
        return (
          <label
            key={tech}
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 2 }}
          >
            <input
              type="checkbox"
              checked={on}
              onChange={() => onToggle(tech)}
              aria-label={`Toggle ${TECH_META[tech].label} generation sites`}
            />
            <span
              style={{
                display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                background: TECH_META[tech].color, border: '1px solid #333', flexShrink: 0,
              }}
            />
            <span>{TECH_META[tech].label}</span>
          </label>
        );
      })}
    </div>
  );
}

// Collapsible legend, self-managing visibility via the same overlayadd/
// remove signal as the layer. Render once inside MapContainer (alongside
// GenerationLayer); it appears only when the overlay is on. Collapsed to
// a header row by default; click the header to expand the swatches.
export function GenerationLegend() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useMapEvents({
    overlayadd: (e) => { if (e.name === OVERLAY_NAME) setVisible(true); },
    overlayremove: (e) => { if (e.name === OVERLAY_NAME) setVisible(false); },
  });

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 24,
        left: 12,
        zIndex: 1000,
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid #ccc',
        borderRadius: 4,
        padding: '8px 10px',
        fontSize: '0.72rem',
        lineHeight: 1.4,
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        maxWidth: 240,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          all: 'unset',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          fontWeight: 700,
        }}
      >
        <span style={{ fontSize: '0.66rem' }}>{expanded ? '▾' : '▸'}</span>
        Generation sites
      </button>
      {expanded && (
        <div style={{ marginTop: 6 }}>
          <div style={{ color: '#666', marginBottom: 6, fontSize: '0.68rem' }}>
            Marker size ∝ capacity (MW)
          </div>
          {ALL_TECHS.map((tech) => (
            <div key={tech} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <span
                style={{
                  display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
                  background: TECH_META[tech].color, border: '1px solid #333', flexShrink: 0,
                }}
              />
              <span>{TECH_META[tech].label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { OVERLAY_NAME as GENERATION_OVERLAY_NAME };
export default GenerationLayer;
